/* eslint-disable */
import { Component } from 'react'
import { connect } from 'react-redux'
import { compose } from 'redux'
import { Helmet } from 'react-helmet'
import { map, last, keys } from 'lodash'
import qs from 'query-string'
import { useQueryClient } from '@tanstack/react-query'

import { babylistUrl, pdpPath } from 'lib/urls'
import { api, PRODUCTS_STALE_TIME } from 'shared/api'
import {
  setProductDataForBookmarklet,
  unsetProductDataForBookmarklet,
} from 'store/lib/bookmarklet'
import { getHistory } from 'lib/history'
import {
  addProductAvailabilitySubscription,
  addProductToCart,
  addProductToRegistry,
  saveCashFundPreferences,
} from 'store/actions'
import {
  getBrandPageUrl,
  getCategoryWithChildrenBySlug,
  getCashFundPreferences,
  getRegistryOwnerId,
  getUserEmail,
  getUserId,
  isProductInCart,
  withFeatureFlags,
} from 'store/reducers'

import { useCatalogCategoryProducts } from '../../api/hooks'

import ProductDetailsPage from './product-details-page'

const history = getHistory({ useRouter: true })

/**
 * This HOC will inject the necessary genericProduct data to maintain
 * current functionality without modifying too much existing code. We can
 * use this to inject genericProduct as prop and use in mapPropsToState etc
 * where we currently do a lot of the coordination around product info that
 * is then passed in to the ProductDetailsPage component.
 *
 * `ProductDetailsQuery` is similar and we could technically just use this
 * HOC and pass the genericProduct data in to `ProductDetailsPage`, but
 * the `ProductDetailsQuery` component is a better place to encapsulate
 * rendering logic around the various fetching states like loading/error.
 * This HOC and the Query component will both pull from the same cache and
 * not result in duplicate requests which is chill 😎.
 */
export const withProductDetailsQueryCompatibility = (WrappedComponent) => {
  /**
   * Need to handle react-router v3 vs v4 compatibility for testing.
   * See note in lib/test-utils about incompatibility.
   */
  const WithProductDetailsQueryCompatibility = (props) => {
    const queryClient = useQueryClient()

    let params
    if (props.params) {
      // react-router v3
      params = props.params
    } else {
      // react-router v4
      params = props.match.params
    }

    const gpId = parseInt(params.gpId)
    // TODO: account for invalid productId param by defaulting
    // to first product in genericProduct, currently handled on the StoreController
    // but we may want to handle on the frontend as well
    const productId = parseInt(params.productId)
    const productKey = [gpId, productId]

    const { data, isLoading } = api.products.useProduct(productKey, {
      onSuccess: (data) => {
        if (!data.genericProduct) return
        api.products.utils.primeUseProductCache(queryClient, data, productKey)
      },
      staleTime: PRODUCTS_STALE_TIME,
    })

    const { data: recommendedProductsResponse } =
      api.productRecommendations.useProductRecommendations(productKey)

    if (isLoading) return null

    return (
      <WrappedComponent
        {...props}
        queryClient={queryClient}
        recommendedProducts={recommendedProductsResponse?.recommendedProducts}
        productDetailsQueryData={{
          genericProduct: data?.genericProduct,
          productId,
          productKey,
        }}
      />
    )
  }

  return WithProductDetailsQueryCompatibility
}

class ProductDetailsWrapper extends Component {
  componentDidMount() {
    let pathWithProductId = blUrls.pdpPath(
      this.props.genericProduct.slug,
      this.props.genericProduct.id,
      this.props.productId
    )
    if (this.props.location.pathname != pathWithProductId) {
      history.replace({
        pathname: pathWithProductId,
        search: this.props.location.search,
        state: { slugSet: this.props.slugSet },
      })
    }
    setProductDataForBookmarklet(
      this.props.genericProduct.products[this.props.productId]
    )
    this.maybeFetchCategoryProducts()
  }

  componentDidUpdate(prevProps) {
    if (prevProps.productId != this.props.productId) {
      // for Babylist scraper
      setProductDataForBookmarklet(
        this.props.genericProduct.products[this.props.productId]
      )
    }
    this.maybeFetchCategoryProducts()
  }

  componentWillUnmount() {
    unsetProductDataForBookmarklet()
  }

  maybeFetchCategoryProducts() {
    const { categorySlug, queryClient } = this.props

    // We don't hydrate category product data from server when landing on a PDP.
    // The query exist, but the data is null so we want to prefetch it.
    if (categorySlug && queryClient) {
      useCatalogCategoryProducts.prefetch(queryClient, categorySlug, {
        heroes: true,
      })
    }
  }

  categorySlug() {
    return last(this.props.slugSet)
  }

  getSeoTitle() {
    const { genericProduct, productId } = this.props
    return (
      genericProduct.products[productId]?.seoTitle || genericProduct.seoTitle
    )
  }

  getSeoDescription() {
    const { genericProduct, productId } = this.props
    return genericProduct.products[productId]?.seoDescription
  }

  render() {
    const { genericProduct, productId } = this.props
    let queryParams = qs.parse(this.props.location.search) || {}
    let regItemId = parseInt(queryParams.reg_item_id) || null
    let registryId = parseInt(queryParams.registry_id) || null
    let isGroupGift = !!queryParams.is_group_gift

    const links = this.props.genericProduct.seoUrl
      ? [
          {
            rel: 'canonical',
            href: babylistUrl(
              pdpPath(genericProduct.slug, genericProduct.id, productId)
            ),
          },
        ]
      : []

    return (
      <div>
        {this.props.genericProduct && (
          <>
            <Helmet link={links} title={this.getSeoTitle()}>
              <meta name="description" content={this.getSeoDescription()} />
            </Helmet>
            <ProductDetailsPage
              activeCategory={this.props.activeCategory}
              addProductAvailabilitySubscription={
                this.props.addProductAvailabilitySubscription
              }
              addedToCart={this.props.addedToCart}
              addedToRegistry={this.props.addedToRegistry}
              addToCart={this.props.addToCart}
              addToRegistry={this.props.addToRegistry}
              brandPageUrl={this.props.brandPageUrl}
              cashFundPreferences={this.props.cashFundPreferences}
              genericProduct={this.props.genericProduct}
              guides={this.props.guides}
              isGroupGift={isGroupGift}
              isRegistryOwner={this.props.isRegistryOwner}
              productId={this.props.productId}
              recommendedProducts={this.props.recommendedProducts}
              registryId={registryId}
              regItemId={regItemId}
              saveCashFundPreferences={this.props.saveCashFundPreferences}
              setActiveModal={this.props.setActiveModal}
              slugSet={this.props.slugSet}
              userEmail={this.props.userEmail}
            />
          </>
        )}
      </div>
    )
  }
}

const mapStateToProps = (state, ownProps) => {
  const { genericProduct, productId, productIds } =
    ownProps.productDetailsQueryData

  const slugSet =
    ownProps.location.state?.slugSet?.length > 0
      ? ownProps.location.state.slugSet
      : map(genericProduct.categories, 'urlSlug')
  let activeCategory = getCategoryWithChildrenBySlug(state, last(slugSet))
  let categorySlug = activeCategory && activeCategory.urlSlug

  return {
    productIds,
    activeCategory,
    brandPageUrl: genericProduct.brandPageUrl,
    categorySlug,
    isRegistryOwner: getRegistryOwnerId(state) == getUserId(state),
    addedToCart: isProductInCart(state, productId),
    cashFundPreferences: getCashFundPreferences(state),
    genericProduct,
    guides: activeCategory && activeCategory.guides,
    productId,
    slugSet,
    userEmail: getUserEmail(state),
  }
}

const mapDispatchToProps = {
  addProductAvailabilitySubscription,
  addProductToCart,
  addProductToRegistry,
  saveCashFundPreferences,
}

const mergeProps = (stateProps, dispatchProps, ownProps) => ({
  ...ownProps,
  ...stateProps,
  addProductAvailabilitySubscription: () =>
    dispatchProps.addProductAvailabilitySubscription(stateProps.productId),
  addToCart: (data) =>
    dispatchProps.addProductToCart(stateProps.productId, data),
  addToRegistry: (regItemFields, options) =>
    dispatchProps.addProductToRegistry(
      stateProps.productId,
      regItemFields,
      options
    ),
  saveCashFundPreferences: (data) =>
    dispatchProps.saveCashFundPreferences(data),
})

export default compose(
  withProductDetailsQueryCompatibility,
  withFeatureFlags(),
  connect(mapStateToProps, mapDispatchToProps, mergeProps)
)(ProductDetailsWrapper)
